import { Button } from "@clockwise/design-system";
import { Visibility, VisibilityOff } from "@clockwise/icons";
import { AttendeeAvatar } from "@clockwise/web-commons/src/ui/AttendeeAvatar";
import { generateScheduleCQL } from "@clockwise/web-commons/src/util/generateCQL";
import { Skeleton } from "@material-ui/lab";
import { range } from "lodash";
import React, { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBoolean } from "usehooks-ts";
import {
  addCalendarIds,
  removeCalendarIds,
} from "../../../../state/actions/multi-calendar.actions";
import { IReduxState } from "../../../../state/reducers/root.reducer";
import { track, TrackingEvents } from "../../../../util/analytics.util";
import { useReadCalendarColors } from "../../../web-app-calendar/hooks/CalendarColorsContext";
import { Collaborator } from "../hooks/useCollaborators";
import { useProcessMessage } from "../hooks/useProcessMessage";

const ScheduleTimeAction = ({ duration, onClick }: { duration: number; onClick: () => void }) => {
  return <Button onClick={onClick} size="mini" variant="outlined">{`${duration}m`}</Button>;
};

const ShowHideCalendarToggle = ({
  onClick,
  isSelected,
}: {
  onClick: () => void;
  isSelected: boolean;
}) => {
  return (
    <Button
      onClick={onClick}
      size="mini"
      variant="outlined"
      startIcon={isSelected ? VisibilityOff : Visibility}
    />
  );
};

const TopCollaborator = ({ collaborator }: { collaborator: Collaborator }) => {
  const dispatch = useDispatch();
  const calendarColors = useReadCalendarColors();
  const { processMessage, canProcessMessage } = useProcessMessage();

  const calendarColor = calendarColors[collaborator.calendarId || ""]?.border;
  const calendarIdsForCalendarPeek = useSelector(
    (state: IReduxState) => state.multiCalendar.calendarIdsForCalendarPeek,
  );
  const selectedCalendarIds = useSelector(
    (state: IReduxState) => state.multiCalendar.calendarIdsToFullyShow,
  );

  const { value: isFocused, setTrue: setFocused, setFalse: setNotFocused } = useBoolean(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const isCollaboratorSelected = [...selectedCalendarIds, ...calendarIdsForCalendarPeek].includes(
    collaborator?.calendarId || "",
  );

  const toggleUser = () => {
    if (!collaborator.calendarId) return;
    track(TrackingEvents.CHAT.NUX.SIDEBAR_COLLABORATOR_VIEW_CALENDAR_CLICKED, {
      isAdding: !isCollaboratorSelected,
    });
    if (isCollaboratorSelected) {
      dispatch(removeCalendarIds([collaborator.calendarId]));
    } else {
      dispatch(addCalendarIds([collaborator.calendarId]));
    }
  };

  const generateCQLForCollaborator = (duration: number) => {
    if (!collaborator.calendarId) return;
    const name = collaborator.fullName || collaborator.calendarId;
    const cql = generateScheduleCQL([collaborator.calendarId], duration);
    track(TrackingEvents.CHAT.NUX.SIDEBAR_COLLABORATOR_QUICK_CREATE_CLICKED, { duration });
    void processMessage(
      `Schedule ${duration} minutes with ${name}`,
      {
        eventMentions: [],
        personMentions: [
          {
            calendarId: collaborator.calendarId,
            name: name,
          },
        ],
      },
      cql,
    );
  };

  // prevent ui flicker on tabbing
  const delayedSetNotfocused = useCallback(() => {
    setTimeout(() => {
      const focusWithin = containerRef.current?.contains(document.activeElement);
      if (!focusWithin) {
        setNotFocused();
      }
    }, 10);
  }, [setNotFocused]);

  let actions: React.JSX.Element[] = [];
  if (isFocused) {
    if (canProcessMessage) {
      actions = [
        <ShowHideCalendarToggle
          key={"togglecal-1"}
          isSelected={isCollaboratorSelected}
          onClick={toggleUser}
        />,
        <ScheduleTimeAction
          key={"time-action-15"}
          duration={15}
          onClick={() => {
            generateCQLForCollaborator(15);
          }}
        />,
        <ScheduleTimeAction
          key={"time-action-30"}
          duration={30}
          onClick={() => {
            generateCQLForCollaborator(30);
          }}
        />,
      ];
    }
    actions.push(
      <ScheduleTimeAction
        key={"time-action-60"}
        duration={60}
        onClick={() => {
          generateCQLForCollaborator(60);
        }}
      />,
    );
  }

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onMouseEnter={setFocused}
      onMouseLeave={setNotFocused}
      onBlur={delayedSetNotfocused}
      onFocus={setFocused}
      className="cw-flex cw-group cw-justify-between cw-items-center cw-rounded-lg cw-p-1 cw-cursor-pointer hover:cw-bg-neutral-inset"
    >
      <div className="cw-flex cw-gap-3 cw-items-center cw-truncate">
        <AttendeeAvatar
          borderColor={isCollaboratorSelected ? calendarColor : undefined}
          calendarId={collaborator.calendarId || undefined}
          displayName={collaborator.fullName || undefined}
          profile={{
            externalImageUrl: collaborator.externalImageUrl,
          }}
          size="large"
        />
        <div className="cw-body-sm cw-font-medium">
          {collaborator.fullName || collaborator.calendarId}
        </div>
      </div>
      <div className="group-hover:cw-visible"></div>
      {actions.length ? (
        <div className="cw-flex cw-gap-0.5">{actions.map((action) => action)}</div>
      ) : null}
    </div>
  );
};

export const TopCollaborators = ({
  topCollaborators,
  loading,
}: {
  topCollaborators: Collaborator[];
  loading: boolean;
}) => {
  if (!topCollaborators.length && !loading) return null;

  return (
    <div className="cw-rounded-lg cw-w-full">
      <div className="cw-body-base cw-text-12 cw-text-subtle cw-font-semibold cw-m-1">
        Collaborators
      </div>
      {loading || !topCollaborators.length
        ? range(6).map((_, index) => {
            return (
              <div
                key={`collab-loading-${index}`}
                className="cw-flex cw-gap-3 cw-items-center cw-my-1 "
              >
                <Skeleton variant="circle" height={24} width={24} />
                <Skeleton variant="rect" height={24} className="cw-flex-1 cw-rounded-lg" />
              </div>
            );
          })
        : topCollaborators.map((collaborator) => (
            <TopCollaborator collaborator={collaborator} key={collaborator.calendarId} />
          ))}
    </div>
  );
};
